import type { LucideIcon } from 'lucide-react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useLocale } from '@/shared/lib/i18n/locale-context';

/**
 * One figure, large enough to read across a room.
 *
 * Adapted 22 August 2026 from TailAdmin's metric card (MIT — `shared/tailadmin/LICENSE.md`), which
 * shipped as a component holding the words "Customers" and the number 3,782. This one holds
 * neither: it takes a label and a value, because every figure the panel shows is one the API knows.
 *
 * ⚠️ **`tabular-nums`, always.** A row of these is read by comparing them, and proportional digits
 * make 1,118 narrower than 8,880 — the eye ranks them by width before it reads either.
 *
 * ⚠️ **The change indicator names its direction in words, not only in colour and an arrow.** Red
 * with a down arrow is two encodings of the same channel for anyone who cannot separate red from
 * green, and this is a screen where "fewer volunteers responding" and "more" must not be confused.
 *
 * ⚠️ **`up` is not `good`.** Rising is green here only because every figure this card currently
 * shows — volunteers, missions closed, participation — is better when it rises. A card for
 * unreviewed applications would want the opposite, which is why `tone` is a separate prop from the
 * sign of the number rather than derived from it.
 */
export function StatCard({
  label,
  value,
  icon: Icon,
  change,
}: {
  label: string;
  value: string | number;
  icon: LucideIcon;

  /** Movement since the previous period, when there is a previous period to compare against. */
  change?: {
    /** Percentage points, unsigned — the direction comes from `direction`. */
    percent: number;
    direction: 'up' | 'down';
    tone: 'good' | 'bad' | 'neutral';

    /** What it is a change *from* — "since last month", "since the shift began". */
    caption: string;
  };
}) {
  const { t, locale } = useLocale();

  const toneClass =
    change?.tone === 'good'
      ? 'bg-success-50 text-success-600'
      : change?.tone === 'bad'
        ? 'bg-error-50 text-error-500'
        : 'bg-gray-100 text-gray-500';

  const Arrow = change?.direction === 'up' ? ArrowUp : ArrowDown;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm md:p-6">
      <span className="flex size-12 items-center justify-center rounded-xl bg-gray-100">
        <Icon aria-hidden className="size-6 text-gray-800" />
      </span>

      <div className="mt-5 flex flex-wrap items-end justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-theme-sm text-gray-500">{label}</span>
          <span className="text-title-sm font-bold tabular-nums text-gray-800">
            {typeof value === 'number' ? value.toLocaleString(locale === 'ar' ? 'ar-SA' : 'en') : value}
          </span>
        </div>

        {change ? (
          <span
            className={`flex items-center gap-1 rounded-full px-2.5 py-0.5 text-theme-xs font-medium ${toneClass}`}
            title={change.caption}
          >
            <Arrow aria-hidden className="size-3.5" />
            <span className="tabular-nums">{change.percent}%</span>
            <span className="sr-only">
              {change.direction === 'up' ? t.stats.increase : t.stats.decrease} — {change.caption}
            </span>
          </span>
        ) : null}
      </div>
    </div>
  );
}
